export type PanelId =
  | 'offers'
  | 'example-builds'
  | 'contact'
  | 'savings'
  | 'diagram'
  | 'workflow-diagram'
  | 'summary'
  | 'booking-confirmed'
  | 'brief-submitted'
  | 'caller-details-form';

export interface NavEntry {
  panelId: PanelId;
  payload: unknown;
}

interface NavStackCallbacks {
  showPanel: (entry: NavEntry) => void;
  hidePanel: (panelId: PanelId) => void;
  onDepthChange: (depth: number) => void;
}

/**
 * Manages a stack of mutually-exclusive modal panels.
 * Inline companions (capture card, context badges, tool pills) are
 * always-visible and don't participate in the stack.
 */
export class NavStack {
  private stack: NavEntry[] = [];
  private readonly callbacks: NavStackCallbacks;

  constructor(callbacks: NavStackCallbacks) {
    this.callbacks = callbacks;
  }

  get depth(): number {
    return this.stack.length;
  }

  get canGoBack(): boolean {
    return this.stack.length > 1;
  }

  get topPanelId(): PanelId | undefined {
    return this.stack.at(-1)?.panelId;
  }

  /** Push a new panel onto the stack. Hides current top first. */
  push(panelId: PanelId, payload: unknown): void {
    if (this.stack.length > 0) {
      this.callbacks.hidePanel(this.stack.at(-1)!.panelId);
    }
    const entry: NavEntry = { panelId, payload };
    this.stack.push(entry);
    this.callbacks.showPanel(entry);
    this.callbacks.onDepthChange(this.stack.length);
  }

  /** Pop current panel, show previous from cache. No-op if depth <= 1. */
  back(): void {
    if (this.stack.length <= 1) return;
    const current = this.stack.pop()!;
    this.callbacks.hidePanel(current.panelId);
    this.callbacks.showPanel(this.stack.at(-1)!);
    this.callbacks.onDepthChange(this.stack.length);
  }

  /** Hide all panels and clear the stack. */
  closeAll(): void {
    for (const entry of this.stack) {
      this.callbacks.hidePanel(entry.panelId);
    }
    this.stack = [];
    this.callbacks.onDepthChange(0);
  }

  /** Silent reset — no hide callbacks. Used when a new call starts. */
  clear(): void {
    this.stack = [];
  }
}
